import { useEffect } from 'react'
import { setAttrReversible } from '@/lib/tenant-app-identity'

/** Set by gen-public-pages.mjs on a dark public page's static head: the app's own colour, from index.html. */
export const APP_THEME_COLOR_ATTR = 'data-app-theme-color'

/**
 * The browser chrome (Safari's tab bar, Android's status bar) takes the colour of the dark public
 * surface while a public page is mounted, instead of the brand orange in index.html, and gets the
 * app's colour back when the visitor goes on into the app (31.10.2026,
 * docs/work/landing-optimise-2026-10-31.md).
 *
 * The first paint of `/` and of the PublicLayout pages already carries the dark value:
 * gen-public-pages.mjs writes it into their heads, with the app's own value beside it in
 * `data-app-theme-color`. The first time this runs, it puts that app value back as the document's
 * base before laying the dark value over it, in the same task, so nothing paints in between. Undoing
 * the layer then lands on the app's colour, never on the head's dark one (Pilot, #245: a visitor who
 * entered on `/` kept the dark colour in the app for the rest of the tab).
 *
 * `active: false` only restores the base: `/` uses it for a signed-in visitor, who is sent on to
 * the dashboard. The colour is read from the live `--surface-public` token, so a palette change
 * follows on its own.
 */
export function usePublicThemeColor(active = true) {
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]')
    if (!meta) return
    const app = meta.getAttribute(APP_THEME_COLOR_ATTR)
    if (app) {
      meta.setAttribute('content', app)
      meta.removeAttribute(APP_THEME_COLOR_ATTR)
    }
    if (!active) return
    const value = getComputedStyle(document.documentElement).getPropertyValue('--surface-public').trim()
    const revert = setAttrReversible(meta, 'content', value || null)
    return () => revert?.()
  }, [active])
}
